import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { error as logError } from '../../utils/logger';
// Inline types (previously from columnEngine)
interface ColumnStep {
  stepNumber: number;
  column: number;
  operation: string;
  calculation: string;
  result: number;
  carry?: number;
  borrow?: number;
  explanation: string;
}
interface ColumnCalculation {
  operation: 'add' | 'subtract' | 'multiply' | 'divide';
  operand1: number;
  operand2: number;
  result: number;
  steps: ColumnStep[];
  visualGrid: ColumnGrid;
}
interface ColumnGrid {
  rows: ColumnRow[];
  columns: number;
}
interface ColumnRow {
  type: 'operand1' | 'operand2' | 'operation' | 'line' | 'result' | 'carry' | 'borrow';
  values: (number | string | null)[];
  label?: string;
}
// Inline calculation functions (previously from columnEngine)
function splitDigits(num: number): number[] {
  return String(num).split('').map(Number).reverse();
}
function joinDigits(digits: number[]): number {
  return parseInt(digits.reverse().join(''));
}
function getColumnName(col: number): string {
  const names = ['eenheden', 'tientallen', 'honderdtallen', 'duizendtallen'];
  return names[col] || `kolom ${col + 1}`;
}
function padWithLeadingNulls(digits: number[], targetLength: number): (number | null)[] {
  const paddedDigits: (number | null)[] = [...digits];
  while (paddedDigits.length < targetLength) {
    paddedDigits.unshift(null);
  }
  return paddedDigits;
}
function calculateAddition(a: number, b: number): ColumnCalculation {
  const digitsA = splitDigits(a);
  const digitsB = splitDigits(b);
  const maxLength = Math.max(digitsA.length, digitsB.length);
  const steps: ColumnStep[] = [];
  const resultDigits: number[] = [];
  let carry = 0;
  for (let col = 0; col < maxLength; col++) {
    const digitA = digitsA[col] || 0;
    const digitB = digitsB[col] || 0;
    const sum = digitA + digitB + carry;
    const digit = sum % 10;
    const newCarry = Math.floor(sum / 10);
    resultDigits.push(digit);
    const columnName = getColumnName(col);
    let explanation = '';
    let calculation = '';
    if (carry > 0) {
      calculation = `${digitA} + ${digitB} + ${carry} = ${sum}`;
      explanation = `Bij de ${columnName}: ${digitA} + ${digitB} + ${carry} (overhouden) = ${sum}. `;
    } else {
      calculation = `${digitA} + ${digitB} = ${sum}`;
      explanation = `Bij de ${columnName}: ${digitA} + ${digitB} = ${sum}. `;
    }
    if (newCarry > 0) {
      explanation += `Dat is ${digit}, en ${newCarry} onthouden.`;
    } else {
      explanation += `Dat is ${digit}.`;
    }
    steps.push({
      stepNumber: col + 1,
      column: col,
      operation: 'optellen',
      calculation,
      result: digit,
      carry: newCarry > 0 ? newCarry : undefined,
      explanation
    });
    carry = newCarry;
  }
  if (carry > 0) {
    resultDigits.push(carry);
  }
  const result = joinDigits([...resultDigits]);
  const visualGrid = createAdditionGrid(a, b, result, steps);
  return {
    operation: 'add',
    operand1: a,
    operand2: b,
    result,
    steps,
    visualGrid
  };
}
function calculateSubtraction(a: number, b: number): ColumnCalculation {
  if (a < b) {
    throw new Error('Kan niet aftrekken: resultaat zou negatief zijn');
  }
  const digitsA = [...splitDigits(a)];
  const digitsB = splitDigits(b);
  const maxLength = Math.max(digitsA.length, digitsB.length);
  const steps: ColumnStep[] = [];
  const resultDigits: number[] = [];
  for (let col = 0; col < maxLength; col++) {
    let digitA = digitsA[col] || 0;
    const digitB = digitsB[col] || 0;
    let borrowed = 0;
    if (digitA < digitB) {
      let borrowCol = col + 1;
      while (borrowCol < digitsA.length && digitsA[borrowCol] === 0) {
        digitsA[borrowCol] = 9;
        borrowCol++;
      }
      if (borrowCol < digitsA.length) {
        digitsA[borrowCol]--;
        digitA += 10;
        borrowed = 1;
      }
    }
    const diff = digitA - digitB;
    resultDigits.push(diff);
    const columnName = getColumnName(col);
    let explanation = '';
    let calculation = '';
    if (borrowed > 0) {
      calculation = `${digitA} - ${digitB} = ${diff}`;
      explanation = `Bij de ${columnName}: ${digitA - 10} is te klein, dus we lenen 1. Nu is het ${digitA}. ${digitA} - ${digitB} = ${diff}.`;
    } else {
      calculation = `${digitA} - ${digitB} = ${diff}`;
      explanation = `Bij de ${columnName}: ${digitA} - ${digitB} = ${diff}.`;
    }
    steps.push({
      stepNumber: col + 1,
      column: col,
      operation: 'aftrekken',
      calculation,
      result: diff,
      borrow: borrowed > 0 ? borrowed : undefined,
      explanation
    });
  }
  const result = joinDigits([...resultDigits]);
  const visualGrid = createSubtractionGrid(a, b, result, steps);
  return {
    operation: 'subtract',
    operand1: a,
    operand2: b,
    result,
    steps,
    visualGrid
  };
}
function calculateMultiplication(a: number, b: number): ColumnCalculation {
  const digitsA = splitDigits(a);
  const digitsB = splitDigits(b);
  const steps: ColumnStep[] = [];
  // For simplicity in this visual, we only support multi-digit × single-digit
  if (digitsB.length > 1) {
    throw new Error('ColumnVisual: Alleen vermenigvuldigen met 1 cijfer wordt ondersteund');
  }
  const digitB = digitsB[0] || 0;
  const resultDigits: number[] = [];
  let carry = 0;
  for (let col = 0; col < digitsA.length; col++) {
    const digitA = digitsA[col] || 0;
    const product = digitA * digitB + carry;
    const digit = product % 10;
    const newCarry = Math.floor(product / 10);
    resultDigits.push(digit);
    const columnName = getColumnName(col);
    let explanation = '';
    let calculation = '';
    if (carry > 0) {
      calculation = `${digitA} × ${digitB} + ${carry} = ${product}`;
      explanation = `Bij de ${columnName}: ${digitA} × ${digitB} = ${digitA * digitB}. Plus ${carry} (onthouden) = ${product}. `;
    } else {
      calculation = `${digitA} × ${digitB} = ${product}`;
      explanation = `Bij de ${columnName}: ${digitA} × ${digitB} = ${product}. `;
    }
    if (newCarry > 0) {
      explanation += `Dat is ${digit}, en ${newCarry} onthouden.`;
    } else {
      explanation += `Dat is ${digit}.`;
    }
    steps.push({
      stepNumber: col + 1,
      column: col,
      operation: 'vermenigvuldigen',
      calculation,
      result: digit,
      carry: newCarry > 0 ? newCarry : undefined,
      explanation
    });
    carry = newCarry;
  }
  if (carry > 0) {
    resultDigits.push(carry);
  }
  const result = joinDigits([...resultDigits]);
  const visualGrid = createMultiplicationGrid(a, b, result, steps);
  return {
    operation: 'add',
    operand1: a,
    operand2: b,
    result,
    steps,
    visualGrid
  };
}
function createMultiplicationGrid(a: number, b: number, result: number, steps: ColumnStep[]): ColumnGrid {
  const digitsA = splitDigits(a).reverse();
  const digitsB = splitDigits(b).reverse();
  const digitsResult = splitDigits(result).reverse();
  const maxLength = Math.max(digitsA.length, digitsB.length, digitsResult.length);
  const paddedDigitsA = padWithLeadingNulls(digitsA, maxLength);
  const paddedDigitsB = padWithLeadingNulls(digitsB, maxLength);
  const paddedDigitsResult = padWithLeadingNulls(digitsResult, maxLength);
  const carryValues = new Array(maxLength).fill(null);
  steps.forEach(step => {
    if (step.carry) {
      const index = maxLength - step.column - 2;
      if (index >= 0) carryValues[index] = step.carry;
    }
  });
  const rows: ColumnRow[] = [{
    type: 'carry',
    values: carryValues,
    label: 'onthouden'
  }, {
    type: 'operand1',
    values: paddedDigitsA
  }, {
    type: 'operand2',
    values: paddedDigitsB,
    label: '×'
  }, {
    type: 'line',
    values: new Array(maxLength).fill('─')
  }, {
    type: 'result',
    values: paddedDigitsResult
  }];
  return {
    rows,
    columns: maxLength
  };
}
function calculateDivision(a: number, b: number): ColumnCalculation {
  // We don't support full long division in ColumnVisual yet,
  // but we provide a fallback message
  throw new Error('ColumnVisual: Staartdeling wordt nog niet ondersteund in deze weergave');
}
function createAdditionGrid(a: number, b: number, result: number, steps: ColumnStep[]): ColumnGrid {
  const digitsA = splitDigits(a).reverse();
  const digitsB = splitDigits(b).reverse();
  const digitsResult = splitDigits(result).reverse();
  const maxLength = Math.max(digitsA.length, digitsB.length, digitsResult.length);
  const paddedDigitsA = padWithLeadingNulls(digitsA, maxLength);
  const paddedDigitsB = padWithLeadingNulls(digitsB, maxLength);
  const paddedDigitsResult = padWithLeadingNulls(digitsResult, maxLength);
  const carryValues = new Array(maxLength).fill(null);
  steps.forEach(step => {
    if (step.carry) {
      const index = maxLength - step.column - 2;
      if (index >= 0) carryValues[index] = step.carry;
    }
  });
  const rows: ColumnRow[] = [{
    type: 'carry',
    values: carryValues,
    label: 'onthouden'
  }, {
    type: 'operand1',
    values: paddedDigitsA
  }, {
    type: 'operand2',
    values: paddedDigitsB,
    label: '+'
  }, {
    type: 'line',
    values: new Array(maxLength).fill('─')
  }, {
    type: 'result',
    values: paddedDigitsResult
  }];
  return {
    rows,
    columns: maxLength
  };
}
function createSubtractionGrid(a: number, b: number, result: number, steps: ColumnStep[]): ColumnGrid {
  const digitsA = splitDigits(a).reverse();
  const digitsB = splitDigits(b).reverse();
  const digitsResult = splitDigits(result).reverse();
  const maxLength = Math.max(digitsA.length, digitsB.length, digitsResult.length);
  const paddedDigitsA = padWithLeadingNulls(digitsA, maxLength);
  const paddedDigitsB = padWithLeadingNulls(digitsB, maxLength);
  const paddedDigitsResult = padWithLeadingNulls(digitsResult, maxLength);
  const borrowValues = new Array(maxLength).fill(null);
  steps.forEach(step => {
    if (step.borrow) {
      const index = maxLength - step.column - 1;
      if (index >= 0) borrowValues[index] = '↓';
    }
  });
  const rows: ColumnRow[] = [{
    type: 'borrow',
    values: borrowValues,
    label: 'lenen'
  }, {
    type: 'operand1',
    values: paddedDigitsA
  }, {
    type: 'operand2',
    values: paddedDigitsB,
    label: '−'
  }, {
    type: 'line',
    values: new Array(maxLength).fill('─')
  }, {
    type: 'result',
    values: paddedDigitsResult
  }];
  return {
    rows,
    columns: maxLength
  };
}
interface ColumnVisualProps {
  operation: 'add' | 'subtract' | 'multiply' | 'divide';
  operand1?: number;
  operand2?: number;
  numbers?: number[];
  showSteps?: boolean;
  currentStep?: number;
  animated?: boolean;
  onStepChange?: (step: number) => void;
  /** 'stimulus' = compact grid only (shown in-question), 'support' = full interactive panel (shown after answer) */
  mode?: 'stimulus' | 'support';
}
export function ColumnVisual({
  operation,
  operand1,
  operand2,
  numbers,
  showSteps = true,
  currentStep: externalStep,
  animated = true,
  onStepChange,
  mode = 'support'
}: ColumnVisualProps) {
  const [internalStep, setInternalStep] = useState(0);
  const currentStep = externalStep !== undefined ? externalStep : internalStep;
  const num1 = operand1 ?? numbers?.[0] ?? 0;
  const num2 = operand2 ?? numbers?.[1] ?? 0;
  if (typeof num1 !== 'number' || typeof num2 !== 'number' || isNaN(num1) || isNaN(num2)) {
    logError('ColumnVisual: Invalid operands', {
      operand1,
      operand2,
      numbers
    });
    return <div className="p-4 bg-slate-100 rounded-lg text-center text-sm text-slate-600" data-id="element-9135">
        <p data-id="element-9136">Rekenvoorbeeld kon niet worden geladen</p>
      </div>;
  }
  let calculation: ColumnCalculation;
  try {
    calculation = operation === 'add' ? calculateAddition(num1, num2) : operation === 'subtract' ? calculateSubtraction(num1, num2) : operation === 'multiply' ? calculateMultiplication(num1, num2) : calculateDivision(num1, num2);
  } catch (error) {
    logError('ColumnVisual: Calculation error', error);
    return <div className="p-4 bg-slate-100 rounded-lg text-center text-sm text-slate-600" data-id="element-9137">
        <p data-id="element-9138">Rekenvoorbeeld kon niet worden berekend</p>
      </div>;
  }
  const {
    visualGrid,
    steps
  } = calculation;
  const maxSteps = steps.length;
  // In stimulus mode, show all result digits revealed (no step-by-step)
  const effectiveStep = mode === 'stimulus' ? maxSteps : currentStep;
  const handleNextStep = () => {
    const newStep = Math.min(currentStep + 1, maxSteps);
    if (onStepChange) {
      onStepChange(newStep);
    } else {
      setInternalStep(newStep);
    }
  };
  const handlePrevStep = () => {
    const newStep = Math.max(currentStep - 1, 0);
    if (onStepChange) {
      onStepChange(newStep);
    } else {
      setInternalStep(newStep);
    }
  };
  const currentStepData = effectiveStep > 0 ? steps[effectiveStep - 1] : null;
  // ── STIMULUS MODE: grid only, no interactive steps ──────────────────────
  if (mode === 'stimulus') {
    return <div className="w-full max-w-sm mx-auto" data-id="element-9139">
        <div className="bg-white rounded-xl p-6 border border-slate-200" data-id="element-9140">
          <div className="font-mono" data-id="element-9141">
            {visualGrid.rows.map((row, rowIndex) => <div key={rowIndex} className="flex justify-center items-end mb-1" data-id="element-9142">
                <div className="w-10 flex items-center justify-end pr-2" data-id="element-9143">
                  {row.label && <span className="text-sm font-bold text-slate-700" data-id="element-9144">
                      {row.label}
                    </span>}
                </div>
                <div className="flex gap-0.5" data-id="element-9145">
                  {row.values.map((value, colIndex) => {
                const isResultRow = row.type === 'result';
                const isCarryOrBorrow = row.type === 'carry' || row.type === 'borrow';
                const isLine = row.type === 'line';
                return <div key={colIndex} className={`
                          flex items-center justify-center font-bold
                          ${isCarryOrBorrow ? 'w-8 h-6 text-xs text-blue-600' : 'w-10 h-11 text-2xl'}
                          ${isLine ? 'border-b-3 border-slate-800' : isCarryOrBorrow ? 'bg-blue-50 rounded-md border border-blue-200' : 'bg-slate-50 rounded-lg border border-slate-200'}
                          ${isResultRow && !isLine ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : !isCarryOrBorrow && !isLine ? 'text-slate-900' : ''}
                        `} data-id="element-9146">
                        {value !== null ? value : ''}
                      </div>;
              })}
                </div>
              </div>)}
          </div>
        </div>
      </div>;
  }
  // ── SUPPORT MODE: full interactive panel (original behavior) ────────────
  return <div className="w-full" data-id="element-9147">
      <div className="bg-white rounded-xl p-8 border-2 border-slate-200 shadow-sm" data-id="element-9148">
        <div className="font-mono" data-id="element-9149">
          {visualGrid.rows.map((row, rowIndex) => <div key={rowIndex} className="flex justify-center items-end mb-1" data-id="element-9150">
              <div className="w-12 flex items-center justify-end pr-3" data-id="element-9151">
                {row.label && <span className="text-base font-bold text-slate-700" data-id="element-9152">
                    {row.label}
                  </span>}
              </div>

              <div className="flex gap-1" data-id="element-9153">
                {row.values.map((value, colIndex) => {
              const isCurrentColumn = currentStepData && visualGrid.columns - colIndex - 1 === currentStepData.column;
              const isResultRow = row.type === 'result';
              const isCarryOrBorrow = row.type === 'carry' || row.type === 'borrow';
              const isLine = row.type === 'line';
              const shouldShow = !isResultRow || currentStep > visualGrid.columns - colIndex - 1;
              return <motion.div key={colIndex} initial={animated ? {
                opacity: 0,
                scale: 0.8
              } : {}} animate={{
                opacity: shouldShow ? 1 : 0.3,
                scale: isCurrentColumn ? 1.1 : 1,
                y: isCurrentColumn ? -2 : 0
              }} transition={{
                duration: 0.3
              }} className={`
                        flex items-center justify-center font-bold
                        ${isCarryOrBorrow ? 'w-10 h-8 text-sm text-blue-600' : 'w-12 h-14 text-3xl'}
                        ${isLine ? 'border-b-4 border-slate-800' : isCarryOrBorrow ? 'bg-blue-50 rounded-md border border-blue-200' : 'bg-slate-50 rounded-lg border-2 border-slate-200'}
                        ${isCurrentColumn && !isLine ? 'ring-2 ring-indigo-400 ring-offset-2 bg-indigo-50 border-indigo-400' : ''}
                        ${isResultRow && !isLine ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : !isCarryOrBorrow && !isLine ? 'text-slate-900' : ''}
                      `} data-id="element-9154">
                      {value !== null ? value : ''}
                    </motion.div>;
            })}
              </div>
            </div>)}
        </div>
      </div>

      {showSteps && <div className="mt-4" data-id="element-9155">
          <AnimatePresence mode="wait" data-id="element-9156">
            {currentStep === 0 ? <motion.div key="intro" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="bg-indigo-50 rounded-lg p-4 border-2 border-indigo-200" data-id="element-9157">
                <p className="text-sm text-indigo-900" data-id="element-9158">
                  <strong data-id="element-9159">Klaar om te beginnen!</strong> We gaan{' '}
                  {operation === 'add' ? 'optellen' : 'aftrekken'} stap voor
                  stap uitleggen.
                </p>
              </motion.div> : currentStepData ? <motion.div key={`step-${currentStep}`} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200" data-id="element-9160">
                <div className="flex items-start gap-3" data-id="element-9161">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold" data-id="element-9162">
                    {currentStep}
                  </div>
                  <div className="flex-1" data-id="element-9163">
                    <h4 className="font-bold text-blue-900 mb-1" data-id="element-9164">
                      {currentStepData.operation.charAt(0).toUpperCase() + currentStepData.operation.slice(1)}{' '}
                      bij de{' '}
                      {['eenheden', 'tientallen', 'honderdtallen', 'duizendtallen'][currentStepData.column]}
                    </h4>
                    <p className="text-sm text-blue-800 mb-2" data-id="element-9165">
                      {currentStepData.explanation}
                    </p>
                    <div className="bg-white rounded px-3 py-2 font-mono text-blue-900 font-bold" data-id="element-9166">
                      {currentStepData.calculation}
                    </div>
                  </div>
                </div>
              </motion.div> : <motion.div key="complete" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="bg-emerald-50 rounded-lg p-4 border-2 border-emerald-200" data-id="element-9167">
                <p className="text-sm text-emerald-900" data-id="element-9168">
                  <strong data-id="element-9169">🎉 Klaar!</strong> Het antwoord is{' '}
                  <strong className="text-2xl" data-id="element-9170">{calculation.result}</strong>
                </p>
              </motion.div>}
          </AnimatePresence>

          <div className="flex gap-2 mt-3" data-id="element-9171">
            <button onClick={handlePrevStep} disabled={currentStep === 0} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white border-2 border-slate-300 rounded-lg font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-9172">
              <ChevronLeft className="w-4 h-4" data-id="element-9173" />
              Vorige stap
            </button>
            <button onClick={handleNextStep} disabled={currentStep >= maxSteps} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-9174">
              Volgende stap
              <ChevronRight className="w-4 h-4" data-id="element-9175" />
            </button>
          </div>

          <div className="mt-3 flex items-center gap-2" data-id="element-9176">
            <span className="text-xs text-slate-600" data-id="element-9177">
              Stap {currentStep} van {maxSteps}
            </span>
            <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden" data-id="element-9178">
              <motion.div className="h-full bg-indigo-600" initial={{
            width: 0
          }} animate={{
            width: `${currentStep / maxSteps * 100}%`
          }} transition={{
            duration: 0.3
          }} data-id="element-9179" />
            </div>
          </div>
        </div>}
    </div>;
}